import React from 'react';

import { Flex, Box } from 'reflexbox';
import {
  Heading,
  Text,
} from 'rebass';
import PageHelmet from 'frontend/components/PageHelmet';

import { Link } from 'react-router';

const PrivacyEmailLink = () => (<a href="mailto:privacy@raidbots.com">privacy@raidbots.com</a>);

export const PrivacyPolicy = () => (
  <>
    <Heading level={1} mb={1}>Privacy Policy</Heading>
    <Text>
      This policy applies to all information collected or submitted on Raidbots’ website, our Discord bot, and any
      other devices and platforms.
    </Text>


    <Heading level={2} mb={1} mt={3}>The Short Version</Heading>
    <Text>
      Raidbots collects as little information as possible, uses that information to provide the services of the site,
      and only shares that information with third parties that assist in performing those services (e.g. sending
      notification emails that you request). We do not sell your information to third parties for commercial purposes
      (e.g. selling lists of email addresses).
    </Text>


    <Heading level={2} mb={1} mt={3}>Information We Collect</Heading>
    <Heading level={4} mb={1} mt={3}>Raidbots Website</Heading>
    <Text my={2}>
      To create an account on Raidbots, you must provide the following information:
    </Text>
    <ul>
      <li><Text>Email address</Text></li>
      <li><Text>Username</Text></li>
      <li><Text>Password</Text></li>
    </ul>
    <Text my={2}>
      Email addresses are used for logging in to the website, resetting your password, sending notifications that you
      request, and customer support services.
    </Text>
    <Text my={2}>
      We store the results of simulations you request and summaries of that information for up to 30 days (sim reports
      are stored for 28 days, archives generated from the &quot;Request Raw Data Archive&quot; tool are stored for up
      to 2 days).
    </Text>
    <Text my={2}>
      Raidbots uses cookies on the website to keep you logged in. Raidbots uses browser LocalStorage to keep track of
      various settings/preferences over time.
    </Text>
    <Text my={2}>
      Our servers store some technical information like IP addresses, browser, or other device information for a
      limited time as part of access logs - these are used to prevent fraud and abuse. In the future, we may use the
      IP address for geolocation to determine things such as default WoW region.
    </Text>

    <Heading level={4} mb={1} mt={3}>Discord and Discord Bot</Heading>
    <Text my={2}>
      If you connect your Raidbots account with your Discord account, we store the read-only token that allows us to
      link your accounts when using the Raidbots Discord bot. This token is used to handle requests to the Raidbots
      Discord bot and apply any rewards or preferences that you have set on your Raidbots account.
    </Text>
    <Text my={2}>
      The Raidbots Discord bot stores aggregate usage information (messages received, simulations run, etc). As with
      any Discord bot, all chat messages that are visible to the bot are inspected to determine if a user is sending
      it a command. When appropriate commands are issued to the bot, it will respond with appropriate functionality
      (running a simulation and reporting the results, setting a preference, etc).
    </Text>
    <Text my={2}>
      We may store anonymized statistics or messages for the purpose of improving the service.
    </Text>
    <Text my={2}>
      The Raidbots Discord Bot adheres to the
      {' '}
      <a
        href="https://discordapp.com/developers/docs/legal"
        target="_blank"
        rel="noopener noreferrer"
      >
        Discord Developer Terms of Service
      </a>.
    </Text>

    <Heading level={4} mb={1} mt={3}>Stripe / Raidbots Premium</Heading>
    <Text my={2}>
      Raidbots collects and stores your country, state/territory, and Postal/ZIP code of your residence to determine
      if sales tax needs to be handled.
    </Text>
    <Text my={2}>
      If you sign up for Raidbots Premium, we store API tokens from Stripe directly on Raidbots. Your credit card
      information is securely stored on Stripe&apos;s servers, not Raidbots.
      Raidbots <em>does</em> have access to this data - it is used to show you your current payment method
      as well as determine applicable taxes.
    </Text>

    <Heading level={4} mb={1} mt={3}>Patreon</Heading>
    <Text my={2}>
      If you connect your Raidbots account and Patreon account, we store the read-only token from Patreon. This token
      is used to determine your pledge status, pledge level, and Discord identity if you have connected to Discord on
      Patreon.
    </Text>

    <Heading level={4} mb={1} mt={3}>Third-Party Analytics</Heading>
    <Text my={2}>
      We use Plausible.io to track basic site traffic and usage information (pages visited, buttons clicked, etc)
      for the purpose of operating the site and developing new features.
    </Text>


    <Heading level={2} mb={1} mt={3}>How We Use Your Information</Heading>
    <Text my={2}>
      We use the information we collect to run and improve the site and to provide customer support.
    </Text>

    <Text my={2}>
      We only share personal information with external parties to perform Raidbots functionality (such as sending
      email). We do not share or sell your personal information with third parties for their commercial purposes.
    </Text>

    <Text my={2}>
      We may disclose or transfer your information in connection with the sale, merger, bankruptcy, sale of assets or
      reorganization of our company.
    </Text>


    <Heading level={2} mb={1} mt={3}>Security</Heading>
    <Text my={2}>
      Raidbots uses a variety of techniques and technologies to take reasonable steps to keep your information secure.
      All website access must occur using HTTPS, passwords are hashed using industry standard methods (currently
      bcrypt), and encryption is used for much of the data at rest.
    </Text>


    <Heading level={2} mb={1} mt={3}>Accessing, Changing, or Deleting Your Information</Heading>
    <Text my={2}>
      You can access or change your data or delete your account and personal data on the
      {' '}<Link to="/account" href="/account">Raidbots account page</Link>.
    </Text>

    <Text my={2}>
      Deleted information may remain in backups for up to 90 days. Backups are only used for disaster recovery.
    </Text>


    <Heading level={2} mb={1} mt={3}>Information for European Union Users</Heading>
    <Text my={2}>
      In order to use our Services, Raidbots collects, stores, and uses your information outside of the European Union
      (“EU”). Without your consent to process your information outside of the EU, we cannot provide you with our
      Services. You may withdraw consent at anytime by deleting your account in the
      {' '}<Link to="/account" href="/account">Raidbots account page</Link>.
    </Text>

    <Text my={2}>
      We will keep your personal data as long as necessary to provide you with Services or to comply with applicable
      laws. You can delete your account and personal data by accessing your account on the
      {' '}<Link to="/account" href="/account">Raidbots account page</Link>.
    </Text>

    <Text my={2}>
      We provide you with tools on the
      {' '}<Link to="/account" href="/account">Raidbots account page</Link>{' '}
      to exercise your rights to access, transfer, rectify or
      erase your personal data. You can exercise your right to restrict or object to processing by deleting your
      account. If you have any questions or need to make an individual request, you can contact us at
      {' '}<PrivacyEmailLink />
    </Text>


    <Heading level={2} mb={1} mt={3}>Children</Heading>
    <Text my={2}>
      Our Services are for users age 16 and over and we do not knowingly collect personal information from children
      under the age of 16. If you are a parent or guardian of a child under the age of 16 and believe he or she has
      disclosed personal information to us please contact us at <PrivacyEmailLink />.
    </Text>


    <Heading level={2} mb={1} mt={3}>Contact Us</Heading>
    <Text my={2}>
      If you have any questions regarding this privacy policy, please contact us at <PrivacyEmailLink />.
    </Text>


    <Heading level={2} mb={1} mt={3}>Privacy Policy Changes</Heading>
    <Text my={2}>
      All changes to the privacy policy will be listed here.
    </Text>

    <Text my={1}>
      <strong>June 6, 2023:</strong> Updated &quot;Third-Party Analytics&quot; to indicate the switch to Plausible.io
      from Google Analytics
    </Text>
    <Text my={1}>
      <strong>July 15, 2020:</strong> Updated &quot;Stripe / Raidbots Premium&quot; to add information collected about
      your residence.
    </Text>
    <Text my={1}>
      <strong>November 4, 2019:</strong> Updated &quot;Information We Collect&quot; to indicate 30 day retention policy
      for simulation data.
    </Text>
    <Text my={1}>
      <strong>July 14, 2018:</strong> Added information about Stripe and Raidbots Premium
    </Text>
    <Text my={1}>
      <strong>May 10, 2018:</strong> Initial version published
    </Text>

  </>
);

const PrivacyPolicyPage = () => (
  <>
    <PageHelmet page="privacy" />
    <Flex justify="center">
      <Box lg={8} sm={12}>
        <PrivacyPolicy />
      </Box>
    </Flex>
  </>
);

export default PrivacyPolicyPage;
